import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { cn } from '@/lib/utils';

interface GaugeChartProps {
  value: number;
  max?: number;
  height?: number;
  label?: string;
  suffix?: string;
  thresholds?: {
    warning: number;
    success: number;
  };
  colors?: {
    danger: string;
    warning: string;
    success: string;
  };
}

const DEFAULT_COLORS = {
  danger: '#ef4444', // red-500
  warning: '#f59e0b', // amber-500
  success: '#22c55e', // green-500
};

const DEFAULT_THRESHOLDS = {
  warning: 50,
  success: 75,
};

export default function GaugeChart({
  value,
  max = 100,
  height = 200,
  label,
  suffix = '%',
  thresholds = DEFAULT_THRESHOLDS,
  colors = DEFAULT_COLORS,
}: GaugeChartProps) {
  const normalizedValue = Math.min(Math.max(value, 0), max);
  const percentage = (normalizedValue / max) * 100;

  // Determine color based on value
  const getColor = () => {
    if (percentage >= thresholds.success) return colors.success;
    if (percentage >= thresholds.warning) return colors.warning;
    return colors.danger;
  };

  const color = getColor();

  // Create data for semi-circle gauge
  const data = [
    { name: 'value', value: normalizedValue },
    { name: 'empty', value: max - normalizedValue },
  ];

  return (
    <div className="relative" style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="70%"
            startAngle={180}
            endAngle={0}
            innerRadius="60%"
            outerRadius="85%"
            dataKey="value"
            stroke="none"
          >
            <Cell fill={color} />
            <Cell fill="#e5e7eb" />
          </Pie>
        </PieChart>
      </ResponsiveContainer>

      {/* Center value display */}
      <div
        className="absolute inset-0 flex flex-col items-center justify-center"
        style={{ paddingTop: height * 0.15 }}
      >
        <span className={cn('text-3xl font-bold', {
          'text-green-600': percentage >= thresholds.success,
          'text-amber-600': percentage >= thresholds.warning && percentage < thresholds.success,
          'text-red-600': percentage < thresholds.warning,
        })}>
          {normalizedValue.toFixed(0)}{suffix}
        </span>
        {label && (
          <span className="text-sm text-muted-foreground mt-1">{label}</span>
        )}
      </div>
    </div>
  );
}
